import { faClipboardQuestion } from "@fortawesome/duotone-light-svg-icons"
import { faSearch } from "@fortawesome/pro-light-svg-icons"
import {
  Button,
  DropdownInput,
  JoonUIColor,
  Modal,
  ModalHeader,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"

import { QuestTemplateItem } from "./QuestTemplateItem"
import { useAddQuestTemplatesModalStore } from "./useAddQuestTemplatesModalStore"
import { useQuestTemplatesStore } from "./useQuestTemplatesStore"
import PageLoader from "../../../components/loading/PageLoader"
import ChildQuestionnaireModal from "../../../components/modals/childQuestionnaireModal/ChildQuestionnaireModal"
import { useChildQuestionnaireModalStore } from "../../../components/modals/childQuestionnaireModal/useChildQuestionnaireModalStore"
import {
  QUERY_KEYS,
  ageGroups,
  templateCategoryDropdownOptions,
} from "../../../constants"
import useIsCoachingAvailable from "../../../hooks/useIsCoachingAvailable"
import useMobile from "../../../hooks/useMobile"
import {
  useFamilyQuery,
  useQuestTemplatesQuery,
} from "../../../networking/queries"
import { FamilyQuerySelect } from "../../../types"
import { QuestTemplateCategory } from "../../../types/quests"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { localStorageItems } from "../../../util/storage"
import CoachUpsellBanner from "../../care/CoachUpsellBanner"
import useCareTeam from "../../care/useCareTeam"
import { useAddEditQuestStore } from "../addEditQuestModal/useAddEditQuestStore"

const AddQuestTemplatesModal = () => {
  const {
    isOpen,
    onClose: onCloseAddTemplateModal,
    showCoachUpsellBanner,
    setShowCoachUpsellBanner,
  } = useAddQuestTemplatesModalStore()
  const { onOpen: onOpenAddEditQuestModal, toggleUser } = useAddEditQuestStore()
  const isCoachingAvailable = useIsCoachingAvailable()

  const {
    ageGroup,
    setAgeGroup,
    questCategory,
    setQuestCategory,
    selectedChild,
    setSelectedChild,
    searchValue,
    setSearchValue,
    filteredTemplates,
    setFilteredTemplates,
  } = useQuestTemplatesStore()
  const { onOpen: onOpenChildQuestionnaireModal } =
    useChildQuestionnaireModalStore()

  const { data, isLoading } = useQuestTemplatesQuery(
    ageGroup?.value as string,
    questCategory.value as QuestTemplateCategory,
    selectedChild?.value as number
  )
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const childDropdownOptions = children
    ? children.map((child) => ({
        label: child.user.name,
        value: child.user.id,
      }))
    : []

  const { hasCoach, isLoading: isCareTeamLoading } = useCareTeam()
  const isMobile = useMobile()

  const queryClient = useQueryClient()

  // filter data
  useEffect(() => {
    if (!data) return
    let filteredData = data
    if (searchValue) {
      filteredData = data?.filter((template) =>
        template.title.toLowerCase().includes(searchValue.toLowerCase())
      )
    }
    setFilteredTemplates(filteredData)
  }, [data, searchValue, setFilteredTemplates])

  // set selected child to first child on first load
  useEffect(() => {
    if (
      children &&
      questCategory.value === QuestTemplateCategory.RECOMMENDED &&
      !selectedChild
    ) {
      setSelectedChild(childDropdownOptions[0])
    }
  }, [children]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const hideBanner =
      localStorage.getItem(localStorageItems.coachUpsellBannerSeen) === "true"
    if (hideBanner) return
    setShowCoachUpsellBanner(true)
  }, [setShowCoachUpsellBanner])

  const onClickCustomQuest = () => {
    onCloseAddTemplateModal()
    onOpenAddEditQuestModal()
    const firstChild = children?.[0]
    if (firstChild) {
      toggleUser(firstChild.user.id)
    }
    trackAnalyticEvent(ANALYTIC_EVENTS.CREATE_CUSTOM_QUEST, {
      source: "empty_state",
    })
  }

  const showBanner =
    !isCareTeamLoading &&
    !hasCoach &&
    showCoachUpsellBanner &&
    isCoachingAvailable

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseAddTemplateModal}
      mobileFull
      animate
      style={{
        width: "min(500px, 100vw)",
        height: isMobile ? "unset" : "95vh",
      }}
    >
      <ModalHeader
        title="Create Quest"
        onClose={onCloseAddTemplateModal}
        style={{ borderBottom: `1px solid ${JoonUIColor.border.default}` }}
      />
      <div className="flex flex-col py-4 px-6 gap-2">
        <TextInput
          name="Search Templates"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          icon={faSearch}
          placeholder="Search for a template..."
          fullWidth
        />
        <div className="flex gap-1">
          <div className="w-3/5">
            <DropdownInput
              label={questCategory.label}
              name="category"
              options={templateCategoryDropdownOptions}
              fullWidth
              onSelect={(option) => setQuestCategory(option)}
            />
          </div>
          <div className="w-2/5">
            {questCategory.value === QuestTemplateCategory.RECOMMENDED ? (
              <DropdownInput
                label={selectedChild?.label}
                name="sort"
                options={childDropdownOptions}
                fullWidth
                onSelect={(option) => setSelectedChild(option)}
              />
            ) : (
              <DropdownInput
                label={ageGroup?.label}
                name="sort"
                options={Object.values(ageGroups)}
                fullWidth
                onSelect={(option) => setAgeGroup(option)}
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-2 relative p-6 w-full">
          {isLoading ? (
            <PageLoader />
          ) : filteredTemplates.length > 0 ? (
            // If data is loaded, show the templates
            filteredTemplates?.map((template, i) => (
              <QuestTemplateItem questTemplate={template} key={i} />
            ))
          ) : data && data.length > 0 && filteredTemplates.length === 0 ? (
            // If data is loaded but no templates match the search
            <Typography variant="bodySmall" textAlign="center">
              No templates to show. Edit your search above!
            </Typography>
          ) : questCategory.value === QuestTemplateCategory.RECOMMENDED ? (
            <div className="flex flex-col gap-2">
              <div className="flex flex-col justify-center items-center border-primary rounded-md p-3 ">
                <Typography variant="bodyBold">
                  Get personalized recommendations
                </Typography>
                <Typography variant="bodyBold" className="mb-2">
                  for {selectedChild?.label}
                </Typography>
                <Button
                  text="Take questionnaire"
                  onClick={() =>
                    onOpenChildQuestionnaireModal(
                      selectedChild?.value as number,
                      () =>
                        queryClient.invalidateQueries([
                          QUERY_KEYS.QUEST_TEMPLATES,
                          ageGroup?.value,
                          QuestTemplateCategory.RECOMMENDED,
                          selectedChild?.value,
                        ])
                    )
                  }
                  icon={faClipboardQuestion}
                  fullWidth
                />
              </div>
              <Typography
                variant="bodySmall"
                textAlign="center"
                color={JoonUIColor.text.secondary}
              >
                To see non-personalized templates, change the category from
                "Smart Recommendations" to something else!
              </Typography>
            </div>
          ) : (
            questCategory.value === QuestTemplateCategory.CUSTOM && (
              <div className="flex items-center justify-center border-primary rounded-md p-3">
                <Typography variant="bodySmallBold" textAlign="center">
                  You don't have any templates saved!
                </Typography>
              </div>
            )
          )}
          {showBanner && <CoachUpsellBanner />}
        </div>
      </div>
      <div className="flex gap-2 items-center flex-col py-4 px-6 bg-neutral-100">
        <Typography variant="bodySmallBold">
          Can't find what you're looking for?
        </Typography>
        <Button
          text="+ Create a custom Quest"
          onClick={onClickCustomQuest}
          fullWidth
        />
      </div>
      <ChildQuestionnaireModal />
    </Modal>
  )
}

export default AddQuestTemplatesModal
